<template>
  <v-menu
    ref="notesMenu"
    v-model="showNotes"
    :content-class="notesType"
    :close-on-content-click="false"
  >
    <template #activator="{ props: activatorProps, isActive }">
      <div v-bind="activatorProps">
        <v-btn
          width="25px"
          min-width="25px"
          :disabled="disabled"
          variant="text"
          rounded
          :title="title || tkey('title')"
          v-on="wrapOpenEvents(isActive)"
        >
          <template #append>
            <span v-if="notifications" class="has-notes-badge" />
          </template>
          <v-icon icon="$notes" />
        </v-btn>
      </div>
    </template>

    <div class="notes">
      <header>
        <h3>{{ title || tkey('title') }}</h3>
        <v-btn density="comfortable" variant="text" icon="$close" @click="close" />
      </header>

      <div class="notes__warning-container">
        <div class="notes__warning-container__scroller">
          <ProgressBar v-if="loading" :message="$t('general.loading')" class="confirm" />

          <div v-else-if="showConfirmDelete" class="confirm">
            <p class="confirm__question">
              {{ tkey('confirmDeleteQuestion') }}
            </p>
            <p class="confirm__warning">
              {{ tkey('confirmDeleteWarning') }}
            </p>
            <v-btn primary variant="flat" width="min-content" class="ma-1" @click="deleteNote">
              {{ $t('actions.delete') }}
            </v-btn>
            <v-btn variant="text" width="min-content" class="ma-1" @click="cancel">
              {{ $t('actions.cancel') }}
            </v-btn>
          </div>

          <div v-for="(note, noteIndex) in notes" v-else :key="note._id" class="notes__container">
            <AssortmentAvatar :username="note.user.username" />
            <div class="notes__bodyText">
              <p class="notes__date">
                {{ formatDate(note.creationDate, getDateFormats.mediumWithTime) }}
              </p>
              <v-menu
                v-if="showOptionsMenu(note)"
                :disabled="isCurrentlyEditing(noteIndex)"
                :close-on-content-click="false"
              >
                <template #activator="{ props }">
                  <v-icon v-bind="props" :color="isCurrentlyEditing(noteIndex) ? 'grey' : 'black'">
                    mdi-dots-horizontal
                  </v-icon>
                </template>
                <v-list class="edit-menu">
                  <v-list-item @click="enableEditMode(noteIndex)">
                    {{ $t('actions.edit') }}
                  </v-list-item>
                  <v-list-item @click="openDeleteConfirm(note._id, noteIndex)">
                    {{ $t('actions.delete') }}
                  </v-list-item>
                </v-list>
              </v-menu>
              <form v-if="isCurrentlyEditing(noteIndex)" class="notes__edit-form">
                <v-textarea
                  v-model="editText"
                  class="notes__text"
                  no-resize
                  variant="solo"
                  hide-details
                  flat
                  autocomplete="off"
                  rows="0"
                  :maxlength="maxCharSize"
                />
                <p>
                  {{ remainingEditCharsMessage }}
                </p>
                <v-btn size="small" variant="text" width="min-content" height="18" @click="cancel">
                  {{ $t('actions.cancel') }}
                </v-btn>
                <v-btn
                  primary
                  size="small"
                  variant="flat"
                  width="min-content"
                  height="18"
                  :disabled="isSaveButtonDisabled(note.message)"
                  @click="update(note)"
                >
                  {{ $t('actions.save') }}
                </v-btn>
              </form>
              <p v-else class="notes__text">
                {{ note.message }}
              </p>
            </div>
          </div>
        </div>
        <form v-if="showPostForm" class="notes__post-form">
          <v-textarea
            v-model="text"
            :placeholder="tkey('addNewPrompt')"
            no-resize
            variant="solo"
            hide-details
            flat
            autocomplete="off"
            rows="3"
            :maxlength="maxCharSize"
          />
          <p>
            {{ remainingCharsMessage }}
          </p>
          <v-btn
            primary
            size="small"
            variant="flat"
            width="min-content"
            height="18"
            :disabled="isPostButtonDisabled"
            @click="post"
          >
            {{ tkey('postButton') }}
          </v-btn>
        </form>
        <span v-else-if="!hasNotes">{{ tkey('noNotes') }}</span>
      </div>
    </div>
  </v-menu>
</template>

<script>
import { size } from 'lodash';
import { mapState } from 'pinia';
import { useContextStore } from '@/stores/context';
import { useDayjs } from '@/composables/use-dayjs';
import { usePermission } from '@/composables/use-permission';
import { useInternationalization } from '@/composables/use-internationalization';

export default {
  props: {
    notes: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxCharSize: {
      type: Number,
      default: 280,
    },
    title: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    notifications: {
      type: Boolean,
      default: false,
    },
    isCannGroup: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { formatDate } = useDayjs();
    const { canEditNotes, canCreateNotes, canDeleteNotes } = usePermission();
    const { tkey } = useInternationalization('notes');

    return {
      canEditNotes,
      canCreateNotes,
      canDeleteNotes,
      formatDate,
      tkey,
    };
  },
  data: () => ({
    showNotes: false,
    text: null,
    editIndex: null,
    editText: null,
    deleteId: null,
    showConfirmDelete: false,
  }),

  computed: {
    ...mapState(useContextStore, ['getDateFormats', 'hasPermission', 'currentUserProfile']),

    notesType() {
      return this.isCannGroup ? 'cann-group-note' : 'notes-wrapper';
    },

    remainingCharsMessage() {
      return this.generateCharsMessage(this.text);
    },

    remainingEditCharsMessage() {
      return this.generateCharsMessage(this.editText);
    },

    isPostButtonDisabled() {
      return !size(this.text);
    },

    showPostForm() {
      return this.canCreateNotes && !this.showConfirmDelete;
    },

    hasNotes() {
      return size(this.notes);
    },
  },
  methods: {
    post() {
      this.$emit('post', { message: this.text });
      this.text = null;
    },

    generateCharsMessage(selectedText) {
      if (!size(selectedText)) return;
      return this.$t('notes.chars.format', [
        this.maxCharSize - selectedText.length || this.$t('notes.chars.limitPrefix'),
      ]);
    },

    enableEditMode(noteIndex) {
      this.editIndex = noteIndex;
      this.editText = this.notes[noteIndex].message;
    },

    isSaveButtonDisabled(message) {
      return !size(this.editText) || this.editText === message;
    },

    cancel() {
      this.editIndex = null;
      this.showConfirmDelete = false;
      this.editText = null;
      this.text = null;
    },

    update(note) {
      this.$emit('update', { message: this.editText, note });
      this.editText = null;
      this.editIndex = null;
      this.text = null;
    },

    deleteNote() {
      this.$emit('delete', { noteId: this.deleteId });
      this.cancel();
    },

    openDeleteConfirm(noteId, noteIndex) {
      this.editIndex = noteIndex;
      this.deleteId = noteId;
      this.showConfirmDelete = true;
    },

    close() {
      this.cancel();
      this.showNotes = false;
    },

    wrapOpenEvents(isActive) {
      const wrappedClick = () => {
        if (!isActive) {
          this.$emit('menuOpen');
        }
      };
      return { click: wrappedClick };
    },

    getInitials(username) {
      return username
        .split(' ')
        .map(part => part[0])
        .join('');
    },

    showOptionsMenu(note) {
      return (
        this.canEditNotes && this.canDeleteNotes && note.user._id === this.currentUserProfile._id
      );
    },

    isCurrentlyEditing(noteIndex) {
      return noteIndex === this.editIndex;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.has-notes-badge {
  height: 0.7rem;
  width: 0.7rem;
  min-width: 0.7rem;
  top: 0;
  right: 0;
  position: absolute;
  border-radius: 1rem;
  background-color: $assortment-negative-action-colour;
}

.notes-wrapper {
  // Footer has a z-index of 199, this prevents the footer blocking the post button
  z-index: 200 !important;
}

.cann-group-note {
  transform: translateX(-77%) translateY(-3rem);
}

.edit-menu {
  padding: 0.5rem 0;
  & > * {
    min-height: 2.3rem;
    color: $assortment-navigation-tab-text-colour;
    font-size: 1.2rem;
    &:hover {
      background-color: $assortment-blue-light;
    }
  }
}

.notes {
  width: 32.2rem;
  background: $assortment-notes-panel-background;
  padding: 0.9rem;
  border-left: 0.4rem solid $assortment-primary-colour;
  box-shadow: $assortment-pop-out-box-shadow;

  .progress-bar {
    padding: 5rem 0;
  }

  header {
    font-size: 1.4rem;
    padding: 0.3rem 0rem 0.9rem 0.6rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__warning-container {
    position: relative;
    width: 100%;
    height: 100%;

    span {
      text-align: center;
      padding: 2rem;
      display: block;
      font-weight: 600;
    }

    &__scroller {
      overflow-y: auto;
      max-height: 45vh;
      display: flex;
      flex-direction: column-reverse;
      width: 100%;
      height: 100%;

      .confirm {
        padding-top: 1rem;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        flex-direction: column;
        text-align: center;
        // Stops some vuetify elements appearing over the confirm overlay
        z-index: 10;

        p {
          font-size: 1.2rem;
          line-height: 1.5rem;
          padding: 0;
          margin: 0;
        }
        &__warning {
          font-weight: 600;
          margin-bottom: 1.8rem !important;
          margin-top: 1.5rem !important;
        }
      }
    }
  }

  &__container {
    display: flex;
    background: $assortment-background;
    padding: 0.7rem 1rem;
    margin-bottom: 0.6rem;
  }

  &__author {
    height: 2.6rem;
    width: 2.6rem;
    text-transform: uppercase;
    color: $assortment-notes-initials-color;
  }

  &__bodyText {
    padding-left: 1rem;
    font-size: 1.2rem;
    align-self: stretch;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    flex-grow: 1;

    .v-menu {
      align-self: end;
    }

    .v-list-item {
      max-height: 2.3rem;
    }
  }

  &__text {
    word-break: break-word;
    margin-bottom: 0;
    width: 100%;
  }

  &__date {
    font-weight: bold;
    margin-bottom: 0.4rem;
  }

  &__post-form {
    padding: 0.6rem 0.9rem;
    background: $assortment-scenarios-colour;
  }
  &__edit-form {
    p {
      width: 100%;
    }
  }

  form {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;

    .v-btn:first-of-type {
      margin-left: auto;
    }

    .v-btn {
      margin-top: 0.6rem;
    }

    p {
      margin: 0;
    }

    :deep(.v-textarea) {
      border-radius: 0;
      box-shadow: 0;
      width: 100%;
    }

    :deep(.v-field__input) {
      padding: 0.4rem 0.8rem !important;
      font-size: 1.2rem;
      line-height: 1.75rem;
      mask-image: none;
    }
  }
}
</style>
